import { useState, useEffect, useCallback } from "react";

interface FetchState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

/**
 * Generic hook for fetching data with loading/error state management.
 * Avoids duplicating the useState + useCallback + useEffect pattern
 * across list and detail pages.
 */
export function useFetchData<T>(
  fetcher: () => Promise<T>,
  deps: unknown[] = [],
): FetchState<T> & { reload: () => void } {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await fetcher();
      setData(result);
    } catch {
      setError("Something went wrong. Please refresh the page.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, deps);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, reload: load };
}
